import React, { useState, useEffect, useMemo } from 'react';
import { SkillConfig } from '../../types/onboarding';
import { Image } from '../../../ui/Image';
import { useCreateSkill, useSkill } from '../../apis/skill-registry';
import { Button } from '../../components';
import { useGetOutboundIntegrations } from '../../apis/outbound-integrations';
import { Select, Input, AccordionV2 } from '@plerionhq/design-system';
import { generatePublicImagePath } from '../../../ui/generatePublicImagePath';

import { useTranslation, Trans } from 'react-i18next';

interface ClickUpSkillSetupProps {
  skill: SkillConfig;
  onComplete: () => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

class MissingClickupRequiredFieldsError extends Error {
  constructor(message?: string) {
    super();
    this.name = 'MissingClickupRequiredFieldsError';
    this.message = message || '';
  }
}

class InvalidClickupListUrl extends Error {
  constructor(message?: string) {
    super();
    this.name = 'InvalidClickupListUrl';
    this.message = message || '';
  }
}

export const ClickUpSkillSetup: React.FC<ClickUpSkillSetupProps> = ({
  skill,
  onComplete,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation('pleri/onboarding');
  const [selectedIntegrationId, setSelectedIntegrationId] =
    useState<string>('');
  const [listUrl, setListUrl] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Error[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: enableSkill, isPending: isEnabling } = useCreateSkill();
  const {
    data: integrations,
    isLoading: isLoadingIntegrations,
    refetch: refetchIntegrations,
  } = useGetOutboundIntegrations();

  // Fetch existing skill configuration if skill is enabled
  const { data: existingSkill } = useSkill(skill.id, skill.enabled);

  const handleIntegrationUpdate = (
    _selectedIndex?: number,
    selected?: { name: string; value: string },
  ): void => {
    if (!selected || typeof selected.value !== 'string') {
      setSelectedIntegrationId('');
      return;
    }
    setSelectedIntegrationId(selected.value);
  };

  const clickUpOptions = useMemo(
    () =>
      integrations
        ?.filter((integration) => integration.type === 'ClickUp')
        .map((integration) => ({
          name: integration.name,
          value: integration.integrationId,
        })) ?? [],
    [integrations],
  );

  // Auto-select if there's only one integration
  useEffect(() => {
    if (clickUpOptions.length === 1 && !selectedIntegrationId) {
      setSelectedIntegrationId(clickUpOptions[0].value);
    }
  }, [clickUpOptions, selectedIntegrationId]);

  // Initialize state with existing configuration
  useEffect(() => {
    if (existingSkill?.config) {
      const config = existingSkill.config;
      if (typeof config.integrationId === 'string') {
        setSelectedIntegrationId(config.integrationId);
      }
      if (typeof config.listUrl === 'string') {
        setListUrl(config.listUrl);
      }
    }
  }, [existingSkill?.config]);

  // Validate list URL
  useEffect(() => {
    const listUrlRegex = /^https:\/\/app\.clickup\.com\/\d+\/v\/li\/\d+$/;
    const errors: Error[] = [];

    if (!listUrl) {
      errors.push(new MissingClickupRequiredFieldsError());
    } else {
      if (!listUrlRegex.test(listUrl)) {
        errors.push(new InvalidClickupListUrl());
      }
    }

    setValidationErrors(errors);
  }, [listUrl]);

  const handleSubmit = async () => {
    if (isDisabled) {
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      await enableSkill({
        skillType: skill.id,
        enabled: true,
        config: {
          integrationId: selectedIntegrationId,
          listUrl: listUrl,
        },
      });
      await onComplete();
    } catch (error) {
      setError(t('failedToConfigureClickUpPleaseTryAgain'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasExistingConfig =
    typeof existingSkill?.config?.integrationId === 'string' ||
    typeof existingSkill?.config?.listUrl === 'string';
  const isReconfiguring = Boolean(
    existingSkill?.enabled || skill.enabled || hasExistingConfig,
  );
  const hasSingleIntegration = clickUpOptions.length === 1;
  const hasNoIntegrations = clickUpOptions.length === 0;

  // Poll for integrations when there are none, so we auto-detect additions
  useEffect(() => {
    if (isLoadingIntegrations || !hasNoIntegrations) {
      return;
    }
    const intervalId = window.setInterval(() => {
      void refetchIntegrations();
    }, 4000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [hasNoIntegrations, isLoadingIntegrations, refetchIntegrations]);

  const isDisabled =
    isLoading ||
    isEnabling ||
    isSubmitting ||
    isLoadingIntegrations ||
    hasNoIntegrations ||
    !selectedIntegrationId ||
    !listUrl?.trim() ||
    validationErrors.length > 0;

  const selectedOption = clickUpOptions.find(
    (option) => option.value === selectedIntegrationId,
  );

  const imageUrl = useMemo(() => {
    const baseUrl = process.env.API_URL || window?.location?.host;
    return generatePublicImagePath({
      src: 'integrations/click-up/copy-list-url.png',
      baseUrl: `https://${baseUrl}`,
    });
  }, []);

  return (
    <div className="mx-auto max-w-2xl">
      {/* 1. Title */}
      <div className="mb-8 text-center">
        <h2 className="text-2xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
          ClickUp
        </h2>
      </div>
      <div className="rounded-lg bg-white p-6 dark:bg-gray-900">
        {/* 2. Logo */}
        <div className="mb-8 text-center">
          <div className="relative mx-auto inline-block">
            <div className="absolute inset-0 animate-[pulse_4s_ease-in-out_infinite] rounded-xl bg-blue-50 blur-xl dark:bg-blue-950/10" />
            {skill.image ? (
              <Image
                src={skill.image.src}
                alt={skill.image.alt}
                className="relative h-20 rounded-xl object-cover"
              />
            ) : (
              <div className="relative flex h-20 w-20 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/20 to-indigo-600/20">
                <span className="text-4xl leading-tight">{skill.icon}</span>
              </div>
            )}
          </div>
        </div>

        {/* 3. Short Description */}
        <div className="mb-8 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {t('iCanCreateAndManageTasksIn')}
          </p>
        </div>

        {/* 4. Learn More Link */}
        <div className="mb-8 text-center">
          <p className="text-sm text-gray-500">
            <a
              href="https://docs.pleri.ai/tools/clickup"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-700"
            >
              {t('clickHereToLearnMore')}
            </a>
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-8 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-950">
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* 5. Form Fields */}
        <div className="mb-8">
          <div className="mx-auto max-w-md">
            {isLoadingIntegrations ? (
              <div className="w-full">
                <div className="flex w-full items-center gap-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 dark:border-gray-600" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('loadingYourClickUpIntegrations')}
                  </p>
                </div>
              </div>
            ) : hasNoIntegrations ? (
              <div className="space-y-4">
                <div className="w-full">
                  <div className="mb-4 rounded-lg bg-blue-50 p-4 dark:bg-blue-950">
                    <div className="flex items-start gap-3">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                        <svg
                          className="h-3 w-3 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-blue-900">
                          {t('addClickUpIntegration')}
                        </p>
                        <p className="mt-1 text-sm text-blue-700">
                          <Trans
                            t={t}
                            i18nKey="visitAddClickUpIntegrationPageToAdd"
                            components={{
                              a: (
                                <a
                                  href="/settings/integrations/add/ClickUp/ClickUp"
                                  className="underline hover:text-blue-800"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={t(
                                    'openAddClickUpIntegrationPageInA',
                                  )}
                                />
                              ),
                            }}
                          />
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {hasSingleIntegration ? (
                  <div className="w-full">
                    <div className="flex w-full items-center gap-2 rounded-lg bg-blue-50 p-2 dark:bg-blue-950">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                        <svg
                          className="h-3 w-3 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <p className="text-sm text-blue-800">
                        {selectedOption?.name || t('clickUpIntegration')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('integration')}
                    </div>
                    <Select
                      key={`clickup-integration-${selectedIntegrationId}`}
                      options={clickUpOptions}
                      defaultSelectedIndex={
                        selectedIntegrationId
                          ? clickUpOptions.findIndex(
                              (option) =>
                                option.value === selectedIntegrationId,
                            )
                          : -1
                      }
                      placeholder={t('chooseYourClickUp')}
                      onUpdate={handleIntegrationUpdate}
                      wrapperClassName="w-full !gap-0 text-sm"
                      hideCloseIcon={true}
                    />
                  </div>
                )}

                {selectedIntegrationId && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <div className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('listUrl')}
                      </div>
                      <Input
                        id="clickup-list-url"
                        placeholder={t('enterClickUpListUrl')}
                        value={listUrl}
                        className="w-full"
                        focusClass="focus:!border-br-primary focus:!outline-none"
                        focusOutlineClass="focus:ring-br-primary"
                        data-testid="clickup-list-url-input"
                        onChange={(e) => {
                          setListUrl(e.target.value);
                        }}
                        disabled={isLoading || isEnabling || isSubmitting}
                      />
                      <div className="mt-1">
                        {validationErrors.some(
                          (error) =>
                            error instanceof MissingClickupRequiredFieldsError,
                        ) ? (
                          <div className="text-sm text-red-600">
                            {t('listUrlIsRequired')}
                          </div>
                        ) : validationErrors.some(
                            (error) => error instanceof InvalidClickupListUrl,
                          ) ? (
                          <div className="text-sm text-red-600">
                            {t('listUrlIsNotValid')}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <AccordionV2
                      headingComponent={
                        <div className="text-sm">
                          {t('toGetTheListUrlClickOn')}
                        </div>
                      }
                      expandComponent={
                        <div>
                          <img
                            src={imageUrl}
                            alt={t('howToGetClickUpListUrl')}
                            className="h-[400px] w-full object-contain p-4"
                          />
                        </div>
                      }
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* 6. Call to Action Button */}
        {!isLoadingIntegrations && !hasNoIntegrations && (
          <div className="mb-6 flex flex-col items-center">
            <Button
              onClick={handleSubmit}
              disabled={isDisabled}
              className={`group relative flex w-full items-center justify-center rounded-lg px-8 py-3 text-sm font-medium transition-all ${
                isDisabled
                  ? 'cursor-not-allowed bg-gray-300 text-gray-500 dark:bg-gray-600 dark:text-gray-400'
                  : ''
              }`}
              data-testid="clickup-activate-button"
            >
              {isReconfiguring
                ? t('updateConfiguration')
                : t('activateClickUp')}
            </Button>
          </div>
        )}

        {/* 7. Cancel or Go Back Button */}
        {!isLoadingIntegrations && !hasNoIntegrations && (
          <div className="flex flex-col items-center">
            <button
              onClick={onCancel}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              {t('common:cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
